import { Link } from 'react-router-dom';
import { getFeatures } from '@4cm/core/features';
import './Legal.css';

/**
 * Adult-facing privacy notice — UK-GDPR Articles 13 + 14 + ICO
 * Children's Code §4. Lives at /legal/privacy. Outside the AuthGate
 * so prospective users + ICO + parents researching us can read it
 * without signing in.
 *
 * v0.1 draft for counsel review. Source-of-truth doc set lives at
 * docs/legal/. When counsel wants to iterate, edit those docs first;
 * this TSX page is the rendered surface and gets re-synced.
 *
 * V1 LAUNCH POSTURE (2026-05-13): the notice template branches on
 * `features.under13PlayerAccounts` (default false). While the flag
 * is off, the under-13 VPC sections are replaced with a deferral
 * notice — under-13 player accounts aren't open yet, no Yoti
 * integration runs, no child-self-rating data is processed. The
 * 13–17 + adult sections stay unchanged. See
 * docs/spec/22a-v1-launch-posture.md.
 *
 * Last reviewed: 2026-05-13 (v1 scope branch).
 */
export function PrivacyNotice() {
  const { under13PlayerAccounts } = getFeatures();

  return (
    <main className="legal-page">
      <div className="legal-page__eyebrow">Legal</div>
      <h1 className="legal-page__title">Privacy Notice</h1>
      <p className="legal-page__lead">
        How 4CM collects, uses, and protects personal data. Effective 2026-05-08.
        {under13PlayerAccounts && (
          <>
            {' '}For our children's version (Flesch-Kincaid grade ≤6, designed for
            9–12 year-olds), see{' '}
            <Link to="/legal/privacy-for-young-players">privacy for young players</Link>.
          </>
        )}
      </p>

      {!under13PlayerAccounts && (
        <div className="legal-page__notice">
          <strong>V1 launch scope:</strong> 4CM is currently open to coaches,
          parents, and players aged 13+. Players under 13 do not have their own
          accounts in this release — their coach manages their development on
          their behalf, and a linked parent can see the coach's view. This
          notice describes the data we process today. When we open up to
          under-13 accounts, the children's-consent flow (Yoti verification,
          parental consent records, child-self-rating data) will activate and
          this notice will be re-issued with the additional sections.
        </div>
      )}

      <h2>Who we are</h2>
      <p>
        4CM is operated by <strong>4CM Ltd</strong> (legal entity finalised pre-launch),
        registered in England &amp; Wales. We are the data controller for personal
        data processed through the platform. Our Data Protection Officer can be
        reached at <a href="mailto:dpo@4cmcoaching.co.uk">dpo@4cmcoaching.co.uk</a>.
        Our ICO registration number is pending and will appear here once issued.
      </p>

      <h2>What we collect</h2>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Examples</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Account &amp; identity</td>
            <td>Name, email, date of birth, club, role (coach / player / parent / admin)</td>
          </tr>
          <tr>
            <td>Coach professional credentials</td>
            <td>DBS certificate number (encrypted at rest), FA Safeguarding cert, expiry dates</td>
          </tr>
          <tr>
            <td>Sport development</td>
            <td>Coach ratings, player self-ratings, journal entries, goal cards</td>
          </tr>
          <tr>
            <td>Special category — health (Art 9)</td>
            <td>Wellness logs (mood, sleep, soreness), bio-band measurements (height, weight, maturity offset)</td>
          </tr>
          <tr>
            <td>Communications</td>
            <td>Messages, coaching notes attached to clips, welfare incident reports</td>
          </tr>
          <tr>
            <td>Media metadata</td>
            <td>Video tag references (URLs, timestamps, corner tags) — videos themselves are NOT stored on 4CM</td>
          </tr>
          <tr>
            <td>Operational telemetry</td>
            <td>Pseudonymised crash logs (Sentry), product usage events (PostHog when shipped, opt-in)</td>
          </tr>
        </tbody>
      </table>

      <h2>Why we collect it (lawful basis per category)</h2>
      <p>
        UK-GDPR Article 6 requires a lawful basis for each processing activity.
        Special category data (health, biometric-adjacent) requires Article 9
        explicit consent. Our complete register is at{' '}
        <code>docs/legal/lawful-basis-register.md</code> in our public repository.
        Summary:
      </p>
      <ul>
        <li><strong>Contract:</strong> account creation for adult coaches and parents; service operation and communications.</li>
        <li><strong>Consent:</strong> all child account creation; wellness logs; bio-band; video clips of minors; AI development analysis (off by default for under-18s).</li>
        <li><strong>Legitimate interests:</strong> coach ratings; AnalyticsSnapshot computation; pseudonymised server logs.</li>
        <li><strong>Legal obligation:</strong> coach DBS / FA Safeguarding cert verification; data subject rights record-keeping; consent receipts.</li>
        <li><strong>Substantial public interest (Art 9(2)(g)):</strong> welfare incident records — safeguarding under Children Act 1989/2004.</li>
      </ul>

      <h2>Who we share it with</h2>
      <p>
        We only share personal data with processors who help us run the service.
        Each is bound by a Data Processing Agreement (Article 28). At v1 launch:
      </p>
      <ul>
        <li><strong>Supabase</strong> — hosting (database, auth, storage, edge functions)</li>
        <li><strong>Anthropic</strong> — AI inference (Brain chat, planner, agent surfaces)</li>
        <li><strong>OpenAI</strong> — text-to-speech (cloud voice, opt-in only)</li>
        <li><strong>Voyage AI</strong> — embeddings (no personal data)</li>
        <li><strong>Modal Labs</strong> — vision compute (player likeness in video frames; no face recognition or biometric template — ever)</li>
        <li><strong>Sentry</strong> — pseudonymised crash reports (functional cookie consent required)</li>
        <li><strong>PostHog</strong> — product analytics (analytics cookie consent required)</li>
        <li><strong>Resend</strong> — transactional email</li>
        {under13PlayerAccounts && (
          <li><strong>Yoti</strong> — UK parental verification + age assurance</li>
        )}
      </ul>
      <p>
        We don't share children's personal data for advertising. We don't sell
        personal data. Full processor list at{' '}
        <code>docs/legal/processor-list.md</code>.
      </p>

      <h2>International transfers</h2>
      <p>
        Some processors are US-based. Transfers are covered by the UK–US Data
        Bridge (Adequacy Decision, June 2023) for DPF-certified processors, and
        Standard Contractual Clauses for the rest.
        {under13PlayerAccounts && (
          <> Yoti is UK-based, so parental verification data does not leave the UK.</>
        )}
      </p>

      <h2>How long we keep it</h2>
      <p>
        Each category has a documented retention window. Defaults for v1:
      </p>
      <ul>
        <li>Coach ratings: 3 years (max)</li>
        <li>Wellness logs: 90 days default; max 12 months when explicitly enabled</li>
        <li>Bio-band measurements: 12 months default; max 3 years with explicit ongoing consent</li>
        <li>Video tag metadata: 1 year default; max 2 years for UK v1</li>
        <li>Welfare incident records: until subject reaches age 25 (legal-hold extensions apply)</li>
        <li>Consent records: account lifetime + 7 years after final processing</li>
        <li>Server logs (Sentry): 30 days</li>
        <li>Analytics events (PostHog): 90 days raw; 12 months aggregate</li>
      </ul>
      <p>
        Full table + automation at <code>docs/legal/data-retention-policy.md</code>.
      </p>

      <h2>Your rights</h2>
      <p>You can:</p>
      <ul>
        <li><strong>See everything we have on you</strong> — <em>Settings → Privacy → Download my data</em>. JSON file downloads to your device immediately.</li>
        <li><strong>Correct anything wrong</strong> — Settings → Profile editor; or message your coach for fields they own.</li>
        <li><strong>Delete your account</strong> — <em>Settings → Privacy → Delete my account</em>. Anonymises ratings, hard-deletes wellness / video / development entries, soft-deletes your user row, signs you out.</li>
        <li><strong>Pause processing</strong> — <em>Settings → Privacy → Pause toggle</em>. Wellness writes + AI analysis stop until you resume. Existing reads continue.</li>
        <li><strong>Object to a specific use</strong> — <em>Settings → Privacy → Object section</em>. Tick the box for any legitimate-interests category you want to stop.</li>
        <li><strong>Lodge a complaint with the ICO</strong> — <a href="https://ico.org.uk/make-a-complaint">ico.org.uk/make-a-complaint</a>.</li>
      </ul>
      <p>
        Statutory response time is 30 days; our internal target is 5 working days.
        We auto-fulfil Access and Erasure inline — no waiting.
      </p>

      <h2>Children's privacy</h2>
      {under13PlayerAccounts ? (
        <>
          <p>
            4CM is designed for children aged 7+ as players. We follow the ICO
            Children's Code (Age Appropriate Design Code), the Data Protection Act
            2018 §9 (UK age of digital consent = 13), and UK-GDPR Article 8.
          </p>
          <ul>
            <li>Under 13: parental consent required before any data collection. Yoti (or an equivalent ICO-recognised provider) verifies the parent.</li>
            <li>13–17: child consents themselves; parents are informed.</li>
            <li>AI development analysis is <strong>off by default</strong> for under-18s — explicit opt-in required.</li>
            <li>Wellness logs are opt-in per category; never displayed in ranked or comparative views.</li>
            <li>Bio-band measurements are never used for selection, exclusion, ranking, or release decisions — sport-science context only.</li>
            <li>No identity recognition, face recognition, or persistent biometric templates — anywhere in the platform, ever.</li>
          </ul>
          <p>
            For the children's version of this notice in plain English with an audio
            option, see <Link to="/legal/privacy-for-young-players">privacy for young players</Link>.
          </p>
        </>
      ) : (
        <>
          <p>
            4CM is designed for children aged 7+ as players, but v1 only opens
            <strong> player accounts for ages 13 and above</strong>. We follow the ICO
            Children's Code (Age Appropriate Design Code), the Data Protection Act
            2018 §9 (UK age of digital consent = 13), and UK-GDPR Article 8.
          </p>
          <ul>
            <li>
              <strong>Under 13 (no player account in v1):</strong> the child's
              coach manages their squad slot (name, age, jersey, position) as
              a "roster entry" — there is no Verifiable Parental Consent
              workflow because there is no child account. A linked parent
              (over 18) can see the coach's view through the parent portal.
              When we open under-13 player accounts in a later release, the
              full VPC flow (Yoti or equivalent) will activate and this notice
              will be re-issued.
            </li>
            <li>13–17: child consents themselves; parents are informed.</li>
            <li>AI development analysis is <strong>off by default</strong> for under-18s — explicit opt-in required.</li>
            <li>Wellness logs (when offered) are opt-in per category; never displayed in ranked or comparative views.</li>
            <li>Bio-band measurements (when offered) are never used for selection, exclusion, ranking, or release decisions — sport-science context only.</li>
            <li>No identity recognition, face recognition, or persistent biometric templates — anywhere in the platform, ever.</li>
          </ul>
        </>
      )}

      <h2>Cookies &amp; local storage</h2>
      <p>
        We use strictly necessary local storage to keep you signed in and
        remember region / voice preferences. Optional categories (functional
        crash reports, product analytics, marketing) are off by default and
        require your consent via the cookie banner.
      </p>

      <h2>Safeguarding</h2>
      <p>
        Coach accounts require an Enhanced DBS check + FA Safeguarding cert.
        Direct coach-to-minor messaging is blocked at the API layer
        {under13PlayerAccounts
          ? '; messages to under-13s always copy a parent.'
          : ' (13–17 players); in v1 there are no under-13 accounts to message.'}
        {' '}
        Welfare concerns route to your club's Welfare Officer, with statutory
        escalation pathways (LADO / NSPCC / police) where required.
      </p>

      <h2>How to contact us</h2>
      <ul>
        <li>Privacy + DSR queries: <a href="mailto:dpo@4cmcoaching.co.uk">dpo@4cmcoaching.co.uk</a></li>
        <li>Welfare concerns: <a href="mailto:welfare@4cmcoaching.co.uk">welfare@4cmcoaching.co.uk</a></li>
        <li>Postal: address pending; published here pre-launch</li>
        <li>ICO: <a href="https://ico.org.uk">ico.org.uk</a></li>
      </ul>

      <h2>Changes to this notice</h2>
      <p>
        We'll notify you
        {under13PlayerAccounts && (
          <> (and your parent if you're under 13)</>
        )}
        {' '}before any material change takes effect. Past versions are kept for
        audit; the version you originally consented under is recorded against
        your account.
      </p>

      <p className="legal-page__footer">
        Version: <strong>v0.1 (DRAFT for counsel review)</strong> · Last updated:
        2026-05-08 · Source: <code>docs/legal/</code> in our public repository.
        Counsel iteration in progress; production version will be re-issued
        before UK launch.
      </p>
    </main>
  );
}
